import {
  TAPNATIVE_IDENTITY_CAPTURE_PATH,
  captureTapNativeIdentity,
  reportTapNativeIdentityCaptureFailure,
} from '../api/tapnativeIdentityCapture';
import { captureTapNativeAimTrace } from '../api/tapnativeAimTrace';

const STATE_KEY = '__medhugTapNativeIdentityCapture';
const PRIMARY_CAPTURE_AD_UNIT_ID = '111819';
const FALLBACK_CAPTURE_AD_UNIT_ID = '111820';
const FALLBACK_CAPTURE_DELAY_MS = 2500;
const MAX_CAPTURE_ATTEMPTS = 2;
const CAPTURE_RETRY_DELAY_MS = 1200;
const TRACE_STRING_LIMIT = 16 * 1024;
const TRACE_BODY_LIMIT = 250 * 1024;
const TRACE_OBJECT_KEY_LIMIT = 200;
const TRACE_ARRAY_LIMIT = 100;
const TRACE_MAX_DEPTH = 6;
const OBSERVED_URL_HOST_PARTS = [
  'aim-tag.hcn.health',
  'hcn.health',
  'tapnative.com',
  'rlcdn.com',
  'deepintent.com',
  'lhmos.com',
];

function isLocalCaptureHost() {
  if (typeof window === 'undefined' || !window.location) return true;
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function cleanValue(value, maxLength = 500) {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (!text || text === 'undefined' || text === 'null') return '';
  return text.slice(0, maxLength);
}

function generateTraceId(prefix = 'trace') {
  try {
    if (window.crypto?.randomUUID) {
      return `${prefix}_${window.crypto.randomUUID()}`;
    }
  } catch (error) {
    // Fall back to a timestamp/random id below.
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function getState() {
  if (typeof window === 'undefined') return null;
  if (!window[STATE_KEY]) {
    window[STATE_KEY] = {
      browserApiObserverInstalled: false,
      contexts: {},
      currentContext: {},
      dataLayer: null,
      failedReportKeys: new Set(),
      inFlightKeys: new Set(),
      observer: null,
      sentKeys: new Set(),
      traceInFlightKeys: new Set(),
      traceSentKeys: new Set(),
      creativeSentKeys: new Set(),
    };
  }
  return window[STATE_KEY];
}

function truncateTraceString(value, maxLength = TRACE_STRING_LIMIT) {
  const text = String(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...[truncated ${text.length - maxLength} chars]`;
}

function traceSafe(value, depth = 0, seen = new WeakSet()) {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return truncateTraceString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function') return `[Function${value.name ? `:${value.name}` : ''}]`;
  if (value instanceof Date) return value.toISOString();
  if (depth >= TRACE_MAX_DEPTH) return '[MaxDepth]';

  if (typeof value === 'object') {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);

    if (Array.isArray(value)) {
      const result = value.slice(0, TRACE_ARRAY_LIMIT).map((entry) => traceSafe(entry, depth + 1, seen));
      if (value.length > TRACE_ARRAY_LIMIT) {
        result.push(`[Truncated ${value.length - TRACE_ARRAY_LIMIT} items]`);
      }
      return result;
    }

    const result = {};
    const keys = Object.keys(value);
    keys.slice(0, TRACE_OBJECT_KEY_LIMIT).forEach((key) => {
      try {
        result[key] = traceSafe(value[key], depth + 1, seen);
      } catch (error) {
        result[key] = `[Unreadable:${cleanValue(error?.message || error, 120)}]`;
      }
    });
    if (keys.length > TRACE_OBJECT_KEY_LIMIT) {
      result.__truncatedKeys = keys.length - TRACE_OBJECT_KEY_LIMIT;
    }
    return result;
  }

  return truncateTraceString(value);
}

function readStorageSnapshot(storage) {
  const values = {};
  if (!storage) return values;

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) continue;
      values[key] = truncateTraceString(storage.getItem(key) || '');
    }
  } catch (error) {
    values.__error = cleanValue(error?.message || error, 300);
  }

  return values;
}

function readCookieValue(name) {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  return (document.cookie || '')
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(prefix))
    ?.slice(prefix.length) || '';
}

function captureBrowserSnapshot() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return {};

  let localStorageSnapshot = {};
  let sessionStorageSnapshot = {};
  try {
    localStorageSnapshot = readStorageSnapshot(window.localStorage);
  } catch (error) {
    localStorageSnapshot = { __error: cleanValue(error?.message || error, 300) };
  }
  try {
    sessionStorageSnapshot = readStorageSnapshot(window.sessionStorage);
  } catch (error) {
    sessionStorageSnapshot = { __error: cleanValue(error?.message || error, 300) };
  }

  const locationSnapshot = window.location
    ? {
        href: window.location.href,
        origin: window.location.origin,
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      }
    : {};

  return traceSafe({
    cookies: document.cookie || '',
    localStorage: localStorageSnapshot,
    sessionStorage: sessionStorageSnapshot,
    windowName: window.name || '',
    location: locationSnapshot,
    referrer: document.referrer || '',
    title: document.title || '',
    navigator: {
      userAgent: window.navigator?.userAgent,
      language: window.navigator?.language,
      languages: window.navigator?.languages,
      platform: window.navigator?.platform,
      vendor: window.navigator?.vendor,
    },
    adxProfileGuid: readCookieValue('adx_profile_guid'),
    adxRetargetingIde: readCookieValue('adx_retargeting_ide'),
  });
}

function normalizeIdentity(identity = {}) {
  return {
    npi: cleanValue(identity.npi_number || identity.npi, 20),
    identityType: cleanValue(identity.identity_type || identity.identityType || identity.itype, 50),
    hcpType: cleanValue(identity.hcp_type || identity.hcpType || identity.htype, 100),
    professionalDesignation: cleanValue(
      identity.professional_designation || identity.professionalDesignation || identity.pd,
      100
    ),
    primarySpecialtyCode: cleanValue(
      identity.primary_specialty_code || identity.primarySpecialtyCode || identity.sc,
      100
    ),
    firstName: cleanValue(identity.first_name || identity.firstName || identity.fn, 100),
    lastName: cleanValue(identity.last_name || identity.lastName || identity.ln, 100),
    email: cleanValue(identity.email || identity.em, 255),
  };
}

function summarizeElementRect(element) {
  if (!element?.getBoundingClientRect) return null;
  const rect = element.getBoundingClientRect();
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

function isVisibleElement(element) {
  if (!element?.getBoundingClientRect || typeof window === 'undefined') return false;
  const rect = element.getBoundingClientRect();
  let style = null;
  try {
    style = window.getComputedStyle(element);
  } catch (error) {
    return false;
  }
  return (
    rect.width > 1 &&
    rect.height > 1 &&
    style?.display !== 'none' &&
    style?.visibility !== 'hidden' &&
    style?.opacity !== '0'
  );
}

function getElementText(element, maxLength = 300) {
  return cleanValue(
    String(element?.innerText || element?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim(),
    maxLength
  );
}

function uniqueStrings(values, maxItems = 20) {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const text = cleanValue(value, 1000);
    if (!text || seen.has(text)) return;
    seen.add(text);
    result.push(text);
  });
  return result.slice(0, maxItems);
}

function normalizeTapNativeClickUrl(value) {
  try {
    const url = new URL(value, window.location.href);
    if (url.hostname !== 'r.tapnative.com' || !url.pathname.includes('/adx-dir-d/click')) {
      return '';
    }
    return url.href;
  } catch (error) {
    return '';
  }
}

function readImageSnapshot(image) {
  return {
    src: cleanValue(image?.currentSrc || image?.src, 2000),
    alt: cleanValue(image?.alt, 300),
    width: image?.naturalWidth || image?.width || 0,
    height: image?.naturalHeight || image?.height || 0,
    visible: isVisibleElement(image),
    rect: summarizeElementRect(image),
  };
}

function captureTapNativeCreativeSnapshot(container, renderStatus = 'observed') {
  if (!container || typeof document === 'undefined') {
    return {
      status: cleanValue(renderStatus, 50) || 'observed',
      creativeCount: 0,
      visibleCreativeCount: 0,
      containerText: '',
      containerRect: null,
      creatives: [],
    };
  }

  const creativeByHref = new Map();
  Array.from(container.querySelectorAll('a[href]')).forEach((link) => {
    const clickUrl = normalizeTapNativeClickUrl(link.href || link.getAttribute('href'));
    if (!clickUrl) return;

    if (!creativeByHref.has(clickUrl)) {
      creativeByHref.set(clickUrl, {
        clickUrl,
        target: cleanValue(link.target, 100),
        visible: false,
        rects: [],
        texts: [],
        images: [],
      });
    }

    const creative = creativeByHref.get(clickUrl);
    const visible = isVisibleElement(link);
    creative.visible = creative.visible || visible;
    creative.rects.push(summarizeElementRect(link));
    creative.texts.push(getElementText(link));
    Array.from(link.querySelectorAll('img')).forEach((image) => {
      creative.images.push(readImageSnapshot(image));
    });
  });

  const creatives = Array.from(creativeByHref.values())
    .map((creative) => {
      const texts = uniqueStrings(creative.texts, 8);
      const images = creative.images.filter((image, index, array) => {
        if (!image.src) return false;
        return array.findIndex((other) => other.src === image.src) === index;
      });
      const visibleRect =
        creative.rects.find((rect) => rect && rect.width > 1 && rect.height > 1) ||
        images.find((image) => image.rect && image.rect.width > 1 && image.rect.height > 1)?.rect ||
        null;

      return {
        clickUrl: cleanValue(creative.clickUrl, 4000),
        target: creative.target,
        title: cleanValue(texts[0], 300),
        advertiser: cleanValue(texts[1], 300),
        texts,
        imageUrl: cleanValue(images.find((image) => image.visible)?.src || images[0]?.src, 2000),
        imageAlt: cleanValue(images.find((image) => image.visible)?.alt || images[0]?.alt, 300),
        images: images.slice(0, 5),
        visible: creative.visible,
        rect: visibleRect,
      };
    })
    .sort((left, right) => {
      const leftRect = left.rect || {};
      const rightRect = right.rect || {};
      return (leftRect.y || 0) - (rightRect.y || 0) || (leftRect.x || 0) - (rightRect.x || 0);
    })
    .map((creative, index) => ({
      creativeIndex: index,
      ...creative,
    }));

  return {
    status: cleanValue(renderStatus, 50) || 'observed',
    creativeCount: creatives.length,
    visibleCreativeCount: creatives.filter((creative) => creative.visible).length,
    containerText: getElementText(container, 1500),
    containerRect: summarizeElementRect(container),
    creatives,
  };
}

function creativeSnapshotKey(context, snapshot) {
  const creativeSignature = snapshot.creatives
    .map((creative) => [creative.clickUrl, creative.title, creative.advertiser, creative.imageUrl].join('~'))
    .join('|');
  return [
    context.traceId || '',
    context.adUnitId || '',
    snapshot.status,
    snapshot.creativeCount,
    creativeSignature,
  ].join('|');
}

function classifyIdentity(identity, error) {
  if (error) return 'error';
  if (identity.npi || identity.email || identity.firstName || identity.lastName) return 'identified';
  if (identity.identityType && identity.identityType !== 'UNK') return 'identified';
  if (identity.identityType === 'UNK') return 'unknown';
  return 'empty';
}

function isValidNpi(value) {
  const npi = cleanValue(value, 20);
  if (!npi) return null;
  if (!/^\d{10}$/.test(npi)) return false;

  const digits = `80840${npi}`.split('').map((digit) => Number(digit));
  const parity = digits.length % 2;
  const total = digits.reduce((sum, digit, index) => {
    let nextDigit = digit;
    if (index % 2 === parity) {
      nextDigit *= 2;
      if (nextDigit > 9) nextDigit -= 9;
    }
    return sum + nextDigit;
  }, 0);

  return total % 10 === 0;
}

function dedupeKey(payload) {
  return [
    payload.source,
    payload.adUnitId || '',
    payload.npi || '',
    payload.identityType || '',
    payload.hcpType || '',
    payload.professionalDesignation || '',
    payload.primarySpecialtyCode || '',
    payload.firstName || '',
    payload.lastName || '',
    payload.email || '',
    payload.rawStatus || '',
  ].join('|');
}

function runApiRequest(factory, payload) {
  return factory(payload);
}

function reportCaptureFailure(payload, error, attempts) {
  const state = getState();
  if (!state) return;

  const key = dedupeKey(payload);
  if (state.failedReportKeys.has(key)) return;
  state.failedReportKeys.add(key);

  const reportPayload = {
    source: 'tapnative_capture_send_failure',
    failureMessage: cleanValue(error?.message || error, 500),
    failedEndpoint: TAPNATIVE_IDENTITY_CAPTURE_PATH,
    attempts,
    failedAtClient: new Date().toISOString(),
    capturePayload: payload,
  };

  try {
    runApiRequest(reportTapNativeIdentityCaptureFailure, reportPayload).catch(() => {});
  } catch (reportError) {
    // Failure reporting is diagnostic; it should never affect page behavior.
  }
}

function sendCaptureAttempt(payload, key, attempt) {
  const state = getState();
  if (!state) return;

  try {
    runApiRequest(captureTapNativeIdentity, payload)
      .then(() => {
        state.sentKeys.add(key);
        state.inFlightKeys.delete(key);
      })
      .catch((error) => {
        if (attempt < MAX_CAPTURE_ATTEMPTS) {
          window.setTimeout(() => {
            sendCaptureAttempt(payload, key, attempt + 1);
          }, CAPTURE_RETRY_DELAY_MS);
          return;
        }

        state.inFlightKeys.delete(key);
        reportCaptureFailure(payload, error, attempt);
      });
  } catch (error) {
    if (attempt < MAX_CAPTURE_ATTEMPTS) {
      window.setTimeout(() => {
        sendCaptureAttempt(payload, key, attempt + 1);
      }, CAPTURE_RETRY_DELAY_MS);
      return;
    }

    state.inFlightKeys.delete(key);
    reportCaptureFailure(payload, error, attempt);
  }
}

function sendCapture(payload) {
  if (isLocalCaptureHost()) return;

  const state = getState();
  if (!state) return;

  const key = dedupeKey(payload);
  if (state.sentKeys.has(key)) return;
  if (state.inFlightKeys.has(key)) return;
  state.inFlightKeys.add(key);

  sendCaptureAttempt(payload, key, 1);
}

function payloadJsonLength(payload) {
  try {
    return JSON.stringify(payload).length;
  } catch (error) {
    return TRACE_BODY_LIMIT + 1;
  }
}

function fitTracePayload(payload) {
  if (payloadJsonLength(payload) <= TRACE_BODY_LIMIT) return payload;

  const reducedPayload = {
    ...payload,
    raw: {
      __truncated: true,
      reason: 'trace_body_limit',
      originalApproxBytes: payloadJsonLength(payload),
      rawSummary: traceSafe(payload.raw, TRACE_MAX_DEPTH - 1),
    },
  };

  if (payloadJsonLength(reducedPayload) <= TRACE_BODY_LIMIT) return reducedPayload;

  return {
    ...payload,
    raw: {
      __truncated: true,
      reason: 'trace_body_limit',
      originalApproxBytes: payloadJsonLength(payload),
    },
    result: payload.result
      ? {
          __truncated: true,
          reason: 'trace_body_limit',
        }
      : payload.result,
  };
}

function sendTraceAttempt(payload, key, attempt) {
  const state = getState();
  if (!state) return;

  try {
    runApiRequest(captureTapNativeAimTrace, payload)
      .then(() => {
        state.traceSentKeys.add(key);
        state.traceInFlightKeys.delete(key);
      })
      .catch(() => {
        if (attempt < MAX_CAPTURE_ATTEMPTS) {
          window.setTimeout(() => {
            sendTraceAttempt(payload, key, attempt + 1);
          }, CAPTURE_RETRY_DELAY_MS);
          return;
        }
        state.traceInFlightKeys.delete(key);
      });
  } catch (error) {
    if (attempt < MAX_CAPTURE_ATTEMPTS) {
      window.setTimeout(() => {
        sendTraceAttempt(payload, key, attempt + 1);
      }, CAPTURE_RETRY_DELAY_MS);
      return;
    }
    state.traceInFlightKeys.delete(key);
  }
}

function buildBasePayload(source, context = {}) {
  const state = getState();
  const registeredAdUnitIds = Object.keys(state?.contexts || {});
  return {
    source,
    traceId: cleanValue(context.traceId, 128),
    adUnitId: cleanValue(context.adUnitId, 32),
    placement: cleanValue(context.placement, 100),
    pageUrl:
      typeof window !== 'undefined' && window.location
        ? cleanValue(window.location.href, 2000)
        : '',
    capturedAtClient: new Date().toISOString(),
    registeredAdUnitIds,
  };
}

function buildTracePayload({
  eventType,
  source,
  context = {},
  rawStatus = 'observed',
  raw = {},
  result = null,
  normalizedIdentity = null,
  hcnTagId = '',
  adxProfileGuid = '',
  latencyMs = null,
  selectedForIdentity = null,
  skipReason = '',
  extra = {},
}) {
  const normalized = normalizedIdentity || {};
  const payload = {
    ...buildBasePayload(source, context),
    eventId: generateTraceId('event'),
    eventType: cleanValue(eventType, 80),
    rawStatus: cleanValue(rawStatus, 50) || 'observed',
    hcnTagId: cleanValue(hcnTagId, 128),
    adxProfileGuid: cleanValue(adxProfileGuid, 128),
    latencyMs: typeof latencyMs === 'number' && Number.isFinite(latencyMs) ? Math.round(latencyMs) : null,
    selectedForIdentity,
    skipReason: cleanValue(skipReason, 255),
    npi: cleanValue(normalized.npi, 20),
    npiValid: isValidNpi(normalized.npi),
    identityType: cleanValue(normalized.identityType, 50),
    normalizedIdentity: traceSafe(normalized),
    raw: traceSafe(raw),
    result: result === null || result === undefined ? null : traceSafe(result),
    extra: traceSafe(extra),
  };

  return fitTracePayload(payload);
}

function sendTrace(event) {
  if (isLocalCaptureHost()) return;

  const state = getState();
  if (!state) return;

  const payload = buildTracePayload(event);
  const key = payload.eventId;
  if (state.traceSentKeys.has(key) || state.traceInFlightKeys.has(key)) return;
  state.traceInFlightKeys.add(key);

  sendTraceAttempt(payload, key, 1);
}

function captureIdentity({ source, context, identity, error, extra = {} }) {
  const normalizedIdentity = normalizeIdentity(identity);
  sendCapture({
    ...buildBasePayload(source, context),
    ...normalizedIdentity,
    rawStatus: classifyIdentity(normalizedIdentity, error),
    hcnTagId: cleanValue(extra.hcnTagId, 128),
    adxProfileGuid: cleanValue(extra.adxProfileGuid, 128),
    errorMessage: cleanValue(error?.message || error, 500),
    extra,
  });
}

function hasPrimaryCaptureContext(state) {
  return Boolean(state?.contexts?.[PRIMARY_CAPTURE_AD_UNIT_ID]);
}

function parseAdUnitFromUrl(src) {
  try {
    const url = new URL(src, window.location.href);
    return cleanValue(url.searchParams.get('aid'), 32);
  } catch (error) {
    return '';
  }
}

function resolveContextFromCurrentScript(state) {
  if (typeof document === 'undefined') return null;
  const currentScript = document.currentScript;
  const src = currentScript?.getAttribute?.('src') || currentScript?.src;
  const adUnitId = parseAdUnitFromUrl(src || '');
  if (adUnitId && state?.contexts?.[adUnitId]) {
    return state.contexts[adUnitId];
  }
  return null;
}

function resolveCommandContext(state, fallbackContext = {}) {
  return resolveContextFromCurrentScript(state) || fallbackContext || state?.currentContext || {};
}

function getIdentitySelectionForContext(state, context = {}) {
  const adUnitId = cleanValue(context.adUnitId, 32);
  if (adUnitId === PRIMARY_CAPTURE_AD_UNIT_ID) {
    return { selectedForIdentity: true, skipReason: '' };
  }
  if (adUnitId === FALLBACK_CAPTURE_AD_UNIT_ID && hasPrimaryCaptureContext(state)) {
    return { selectedForIdentity: false, skipReason: 'primary_ad_unit_present' };
  }
  if (adUnitId === FALLBACK_CAPTURE_AD_UNIT_ID) {
    return { selectedForIdentity: true, skipReason: 'fallback_selected_if_primary_absent' };
  }
  return { selectedForIdentity: false, skipReason: 'unsupported_ad_unit' };
}

function captureIdentityForAdUnit({ source, context, identity, error, extra = {} }) {
  const state = getState();
  if (!state) return;

  const adUnitId = cleanValue(context?.adUnitId, 32);
  const captureArgs = { source, context, identity, error, extra };

  if (adUnitId === FALLBACK_CAPTURE_AD_UNIT_ID && !hasPrimaryCaptureContext(state)) {
    window.setTimeout(() => {
      if (!hasPrimaryCaptureContext(state)) {
        captureIdentity(captureArgs);
      }
    }, FALLBACK_CAPTURE_DELAY_MS);
    return;
  }

  if (adUnitId !== PRIMARY_CAPTURE_AD_UNIT_ID && hasPrimaryCaptureContext(state)) {
    return;
  }

  captureIdentity(captureArgs);
}

function parseTapNativeCustomEx(customEx) {
  const parsed = {};
  cleanValue(customEx, 4000)
    .split('~~~')
    .forEach((entry) => {
      const separatorIndex = entry.indexOf(':');
      if (separatorIndex === -1) return;
      const key = entry.slice(0, separatorIndex);
      const value = entry.slice(separatorIndex + 1);
      parsed[key] = value;
    });
  return parsed;
}

function urlMatchesObservedDomains(value) {
  if (!value) return false;
  let hostname = '';
  try {
    hostname = new URL(String(value), window.location.href).hostname;
  } catch (error) {
    return OBSERVED_URL_HOST_PARTS.some((part) => String(value).includes(part));
  }
  return OBSERVED_URL_HOST_PARTS.some((part) => hostname === part || hostname.endsWith(`.${part}`));
}

function urlSearchParamsToObject(searchParams) {
  const result = {};
  searchParams.forEach((value, key) => {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      result[key] = Array.isArray(result[key]) ? [...result[key], value] : [result[key], value];
      return;
    }
    result[key] = value;
  });
  return result;
}

function captureBrowserApiObserved({ operation, url, method = '', body = undefined, context = null, extra = {} }) {
  if (!urlMatchesObservedDomains(url)) return;

  const state = getState();
  if (!state) return;

  const resolvedContext = context || resolveContextFromCurrentScript(state) || state.currentContext || {};
  sendTrace({
    eventType: 'browser_api_observed',
    source: 'browser_api',
    context: resolvedContext,
    rawStatus: 'observed',
    raw: {
      operation,
      url,
      method,
      body,
      extra,
    },
  });
}

function captureTapNativeAdScript(src) {
  let url;
  try {
    url = new URL(src);
  } catch (error) {
    return;
  }

  if (url.hostname !== 'health.tapnative.com' || !url.pathname.includes('/adx-dir-d/ads-1p/')) {
    return;
  }

  const adUnitId = cleanValue(url.searchParams.get('aid'), 32);
  const state = getState();
  const context = state?.contexts?.[adUnitId] || state?.currentContext || {};
  const contextWithAdUnit = {
    ...context,
    adUnitId: adUnitId || context.adUnitId,
  };
  const customEx = url.searchParams.get('adx_custom_ex') || '';
  const identity = parseTapNativeCustomEx(customEx);
  const normalizedIdentity = normalizeIdentity(identity);
  const adxProfileGuid = cleanValue(url.searchParams.get('adx_profile_guid'), 128);
  const selection = getIdentitySelectionForContext(state, contextWithAdUnit);

  sendTrace({
    eventType: 'tapnative_ad_request_observed',
    source: 'tapnative_ad_request',
    context: contextWithAdUnit,
    rawStatus: customEx ? classifyIdentity(normalizedIdentity, null) : 'empty',
    raw: {
      scriptSrc: src,
      queryParams: urlSearchParamsToObject(url.searchParams),
      adxCustomEx: customEx,
      parsedCustomEx: identity,
      browserSnapshot: captureBrowserSnapshot(),
    },
    normalizedIdentity,
    adxProfileGuid,
    selectedForIdentity: selection.selectedForIdentity,
    skipReason: selection.skipReason,
    extra: {
      aid: adUnitId,
      nid: cleanValue(url.searchParams.get('nid'), 32),
      hasCustomEx: Boolean(customEx),
    },
  });

  captureIdentityForAdUnit({
    source: 'tapnative_ad_request',
    context: contextWithAdUnit,
    identity,
    extra: {
      adxProfileGuid,
      aid: adUnitId,
      nid: cleanValue(url.searchParams.get('nid'), 32),
      hasCustomEx: Boolean(customEx),
    },
  });
}

export function captureTapNativeAdCreatives({ adUnitId, placement, container, status = 'observed' } = {}) {
  if (isLocalCaptureHost()) return;

  const state = getState();
  if (!state) return;

  const normalizedAdUnitId = cleanValue(adUnitId, 32);
  const existingContext = normalizedAdUnitId ? state.contexts[normalizedAdUnitId] : null;
  const context = {
    adUnitId: normalizedAdUnitId,
    placement: cleanValue(placement || existingContext?.placement, 100),
    traceId: existingContext?.traceId || generateTraceId('tapnative'),
  };

  if (context.adUnitId && !existingContext) {
    state.contexts[context.adUnitId] = context;
  }

  const snapshot = captureTapNativeCreativeSnapshot(container, status);
  const key = creativeSnapshotKey(context, snapshot);
  if (state.creativeSentKeys.has(key)) return;
  state.creativeSentKeys.add(key);

  sendTrace({
    eventType: 'tapnative_ad_creative_observed',
    source: 'tapnative_creative',
    context,
    rawStatus: snapshot.creativeCount ? 'observed' : 'empty',
    raw: snapshot,
    extra: {
      creativeCount: snapshot.creativeCount,
      visibleCreativeCount: snapshot.visibleCreativeCount,
      renderStatus: snapshot.status,
    },
  });
}

function inspectScriptNode(node) {
  if (!node || node.nodeType !== 1 || node.tagName !== 'SCRIPT') return;
  const src = node.getAttribute('src');
  if (!src) return;
  captureBrowserApiObserved({
    operation: 'script_element_observed',
    url: src,
    context: getState()?.contexts?.[parseAdUnitFromUrl(src)] || null,
  });
  captureTapNativeAdScript(src);
}

function installScriptObserver(state) {
  if (state.observer || typeof MutationObserver === 'undefined') return;

  state.observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        inspectScriptNode(node);
        if (node?.querySelectorAll) {
          node.querySelectorAll('script[src]').forEach(inspectScriptNode);
        }
      });
    });
  });

  state.observer.observe(document.documentElement || document.body, {
    childList: true,
    subtree: true,
  });

  document.querySelectorAll('script[src]').forEach(inspectScriptNode);
}

function isSignalCommand(command) {
  return (
    command &&
    typeof command === 'object' &&
    command.length >= 3 &&
    command[1] === 'signal' &&
    typeof command[2] === 'function'
  );
}

function wrapSignalCommand(command, state) {
  if (!isSignalCommand(command)) return command;

  const originalCallback = command[2];
  if (originalCallback.__medhugTapNativeWrapped) return command;
  const contextAtWrap = resolveCommandContext(state, state.currentContext || {});
  const hcnTagId = cleanValue(command[0], 128);
  const requestStartedAt = Date.now();
  const selection = getIdentitySelectionForContext(state, contextAtWrap);

  sendTrace({
    eventType: 'aim_signal_requested',
    source: 'aim_datalayer',
    context: contextAtWrap,
    rawStatus: 'requested',
    hcnTagId,
    selectedForIdentity: selection.selectedForIdentity,
    skipReason: selection.skipReason,
    raw: {
      hcnTagId,
      command: traceSafe(command),
      browserSnapshot: captureBrowserSnapshot(),
    },
  });

  const wrappedCallback = function medhugTapNativeSignalCapture(error, identity) {
    const normalizedIdentity = normalizeIdentity(identity);
    const rawStatus = classifyIdentity(normalizedIdentity, error);
    const latestSelection = getIdentitySelectionForContext(state, contextAtWrap);

    sendTrace({
      eventType: 'aim_signal_returned',
      source: 'aim_signal',
      context: contextAtWrap,
      rawStatus,
      hcnTagId,
      latencyMs: Date.now() - requestStartedAt,
      selectedForIdentity: latestSelection.selectedForIdentity,
      skipReason: latestSelection.skipReason,
      raw: {
        hcnTagId,
        browserSnapshot: captureBrowserSnapshot(),
      },
      result: {
        error,
        identity,
      },
      normalizedIdentity,
    });

    captureIdentityForAdUnit({
      source: 'aim_signal',
      context: contextAtWrap,
      identity,
      error,
      extra: {
        hcnTagId,
      },
    });
    return originalCallback.apply(this, arguments);
  };
  wrappedCallback.__medhugTapNativeWrapped = true;

  try {
    command[2] = wrappedCallback;
    return command;
  } catch (error) {
    const clonedCommand = Array.prototype.slice.call(command);
    clonedCommand[2] = wrappedCallback;
    return clonedCommand;
  }
}

function patchDataLayerArray(dataLayer, state) {
  if (!Array.isArray(dataLayer)) return dataLayer;
  if (dataLayer.__medhugTapNativePatched) return dataLayer;

  for (let index = 0; index < dataLayer.length; index += 1) {
    dataLayer[index] = wrapSignalCommand(dataLayer[index], state);
  }

  const originalPush = dataLayer.push.bind(dataLayer);
  Object.defineProperty(dataLayer, 'push', {
    configurable: true,
    writable: true,
    value: function medhugTapNativeDataLayerPush(...items) {
      return originalPush(...items.map((item) => wrapSignalCommand(item, state)));
    },
  });
  Object.defineProperty(dataLayer, '__medhugTapNativePatched', {
    configurable: true,
    value: true,
  });
  return dataLayer;
}

function installAimDataLayerPatch(state) {
  if (state.dataLayer) return;

  let activeDataLayer = patchDataLayerArray(window.aimDataLayer || [], state);
  state.dataLayer = activeDataLayer;

  try {
    Object.defineProperty(window, 'aimDataLayer', {
      configurable: true,
      get() {
        return activeDataLayer;
      },
      set(nextDataLayer) {
        activeDataLayer = patchDataLayerArray(
          Array.isArray(nextDataLayer) ? nextDataLayer : [],
          state
        );
        state.dataLayer = activeDataLayer;
      },
    });
  } catch (error) {
    window.aimDataLayer = activeDataLayer;
  }
}

function patchUrlSetter(proto, propertyName, operation) {
  const descriptor = Object.getOwnPropertyDescriptor(proto, propertyName);
  if (!descriptor?.set || descriptor.set.__medhugTapNativeWrapped) return;

  const originalSetter = descriptor.set;
  const wrappedSetter = function medhugTapNativeUrlSetter(value) {
    captureBrowserApiObserved({
      operation,
      url: value,
      context: getState()?.contexts?.[parseAdUnitFromUrl(value)] || null,
    });
    return originalSetter.call(this, value);
  };
  wrappedSetter.__medhugTapNativeWrapped = true;

  try {
    Object.defineProperty(proto, propertyName, {
      ...descriptor,
      set: wrappedSetter,
    });
  } catch (error) {
    // Some browsers/extensions may lock descriptors. Missing this hook should not affect ads.
  }
}

function installBrowserApiObserver(state) {
  if (state.browserApiObserverInstalled) return;
  state.browserApiObserverInstalled = true;

  if (typeof window.fetch === 'function' && !window.fetch.__medhugTapNativeWrapped) {
    const originalFetch = window.fetch;
    const wrappedFetch = function medhugTapNativeFetch(input, init = {}) {
      const url = typeof input === 'string' ? input : input?.url;
      captureBrowserApiObserved({
        operation: 'fetch',
        url,
        method: init?.method || input?.method || 'GET',
        body: init?.body,
      });
      return originalFetch.apply(this, arguments);
    };
    wrappedFetch.__medhugTapNativeWrapped = true;
    window.fetch = wrappedFetch;
  }

  if (typeof window.XMLHttpRequest !== 'undefined') {
    const xhrProto = window.XMLHttpRequest.prototype;
    if (xhrProto.open && !xhrProto.open.__medhugTapNativeWrapped) {
      const originalOpen = xhrProto.open;
      const wrappedOpen = function medhugTapNativeXhrOpen(method, url) {
        this.__medhugTapNativeTrace = { method, url };
        return originalOpen.apply(this, arguments);
      };
      wrappedOpen.__medhugTapNativeWrapped = true;
      xhrProto.open = wrappedOpen;
    }
    if (xhrProto.send && !xhrProto.send.__medhugTapNativeWrapped) {
      const originalSend = xhrProto.send;
      const wrappedSend = function medhugTapNativeXhrSend(body) {
        const meta = this.__medhugTapNativeTrace || {};
        captureBrowserApiObserved({
          operation: 'xhr',
          url: meta.url,
          method: meta.method,
          body,
        });
        return originalSend.apply(this, arguments);
      };
      wrappedSend.__medhugTapNativeWrapped = true;
      xhrProto.send = wrappedSend;
    }
  }

  if (window.navigator?.sendBeacon && !window.navigator.sendBeacon.__medhugTapNativeWrapped) {
    const originalSendBeacon = window.navigator.sendBeacon.bind(window.navigator);
    const wrappedSendBeacon = function medhugTapNativeSendBeacon(url, data) {
      captureBrowserApiObserved({
        operation: 'send_beacon',
        url,
        method: 'POST',
        body: data,
      });
      return originalSendBeacon(url, data);
    };
    wrappedSendBeacon.__medhugTapNativeWrapped = true;
    try {
      window.navigator.sendBeacon = wrappedSendBeacon;
    } catch (error) {
      // sendBeacon can be read-only in some browser contexts.
    }
  }

  if (typeof Element !== 'undefined' && Element.prototype.setAttribute && !Element.prototype.setAttribute.__medhugTapNativeWrapped) {
    const originalSetAttribute = Element.prototype.setAttribute;
    const wrappedSetAttribute = function medhugTapNativeSetAttribute(name, value) {
      if (String(name || '').toLowerCase() === 'src' || String(name || '').toLowerCase() === 'href') {
        captureBrowserApiObserved({
          operation: 'set_attribute',
          url: value,
          extra: {
            tagName: this?.tagName,
            attributeName: name,
          },
          context: getState()?.contexts?.[parseAdUnitFromUrl(value)] || null,
        });
      }
      return originalSetAttribute.apply(this, arguments);
    };
    wrappedSetAttribute.__medhugTapNativeWrapped = true;
    Element.prototype.setAttribute = wrappedSetAttribute;
  }

  if (typeof HTMLScriptElement !== 'undefined') {
    patchUrlSetter(HTMLScriptElement.prototype, 'src', 'script_src_set');
  }
  if (typeof HTMLImageElement !== 'undefined') {
    patchUrlSetter(HTMLImageElement.prototype, 'src', 'image_src_set');
  }

  window.addEventListener('message', (event) => {
    const serializedData = traceSafe(event.data);
    const dataText = typeof serializedData === 'string' ? serializedData : JSON.stringify(serializedData || {});
    if (!urlMatchesObservedDomains(event.origin) && !OBSERVED_URL_HOST_PARTS.some((part) => dataText.includes(part))) {
      return;
    }

    captureBrowserApiObserved({
      operation: 'message_received',
      url: event.origin || window.location.href,
      extra: {
        origin: event.origin,
        data: serializedData,
      },
    });
  });
}

export function registerTapNativeIdentityCapture({ adUnitId, placement } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (isLocalCaptureHost()) return;

  const state = getState();
  const normalizedAdUnitId = cleanValue(adUnitId, 32);
  const existingContext = normalizedAdUnitId ? state.contexts[normalizedAdUnitId] : null;
  const context = {
    adUnitId: normalizedAdUnitId,
    placement: cleanValue(placement, 100),
    traceId: existingContext?.traceId || generateTraceId('tapnative'),
  };

  if (context.adUnitId) {
    state.contexts[context.adUnitId] = context;
  }
  state.currentContext = context;

  installBrowserApiObserver(state);
  installAimDataLayerPatch(state);
  installScriptObserver(state);

  sendTrace({
    eventType: 'capture_registered',
    source: 'tapnative_capture',
    context,
    rawStatus: 'observed',
    raw: {
      browserSnapshot: captureBrowserSnapshot(),
    },
  });
}
